import { useEffect, useRef, useState, useCallback } from "react";
import { Bell, Heart, MessageSquare, UserPlus, AtSign } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

const phrase = (n) => {
  const name = n.actor?.display_name || n.actor?.username || "Someone";
  if (n.kind === "like") return `${name} loved your post`;
  if (n.kind === "comment") return `${name} left a comment`;
  if (n.kind === "follow") return `${name} is now following you`;
  if (n.kind === "mention") return `${name} mentioned you`;
  return name;
};

const Glyph = ({ kind }) => {
  const common = { size: 12, strokeWidth: 1.6 };
  if (kind === "like") return <Heart {...common} color="var(--brand-accent)" />;
  if (kind === "comment") return <MessageSquare {...common} color="var(--text-primary)" />;
  if (kind === "follow") return <UserPlus {...common} color="var(--text-primary)" />;
  if (kind === "mention") return <AtSign {...common} color="var(--brand-accent)" />;
  return <Bell {...common} />;
};

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  const fetchCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setCount(data.count || 0);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    return () => clearInterval(id);
  }, [fetchCount]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (open && wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const openPanel = async () => {
    setOpen((v) => !v);
    if (open) return;
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 30 } });
      setItems(data || []);
      if (count > 0) {
        api.post("/notifications/mark-read").then(() => setCount(0)).catch(() => {});
      }
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (n) => {
    setOpen(false);
    if (n.kind === "follow" && n.actor?.username) {
      navigate(`/profile/${n.actor.username}`);
    } else if (n.post?.id) {
      navigate(`/post/${n.post.id}`);
    } else if (n.actor?.username) {
      navigate(`/profile/${n.actor.username}`);
    }
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button
        type="button"
        data-testid="notif-bell-button"
        onClick={openPanel}
        aria-label="Notifications"
        title="Notifications"
        style={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          padding: 4,
          display: "flex",
          alignItems: "center",
          position: "relative",
        }}
      >
        <Bell size={20} strokeWidth={1.4} color="var(--text-primary)" />
        {count > 0 && (
          <span
            data-testid="notif-bell-badge"
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              minWidth: 16,
              height: 16,
              padding: "0 4px",
              borderRadius: 999,
              background: "var(--brand-accent)",
              color: "#FAF9F5",
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: 0,
              display: "grid",
              placeItems: "center",
              lineHeight: 1,
              border: "1.5px solid var(--surface)",
            }}
          >
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-dropdown"
          style={{
            position: "absolute",
            top: 44,
            right: 0,
            width: 340,
            maxHeight: 480,
            overflowY: "auto",
            background: "var(--surface)",
            border: "1px solid var(--border-default)",
            boxShadow: "0 12px 40px rgba(51,51,51,0.10)",
            zIndex: 200,
          }}
        >
          <div
            style={{
              padding: "14px 18px",
              borderBottom: "1px solid var(--border-default)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div className="overline" style={{ color: "var(--brand-accent)" }}>
              Activity
            </div>
            <div className="overline" style={{ fontSize: 10, opacity: 0.6 }}>
              {items.length} {items.length === 1 ? "entry" : "entries"}
            </div>
          </div>

          {loading ? (
            <div style={{ padding: "32px 18px", textAlign: "center" }}>
              <p className="editorial-body" style={{ fontStyle: "italic", color: "var(--text-muted)", fontSize: 13 }}>
                Listening for whispers…
              </p>
            </div>
          ) : items.length === 0 ? (
            <div style={{ padding: "40px 24px", textAlign: "center" }}>
              <Bell size={20} strokeWidth={1.2} color="var(--text-muted)" style={{ opacity: 0.5 }} />
              <p
                className="editorial-body"
                style={{ fontStyle: "italic", color: "var(--text-muted)", fontSize: 14, marginTop: 12, lineHeight: 1.5 }}
              >
                The salon is quiet. When someone loves, comments on, or follows you,
                you&apos;ll find their note here.
              </p>
            </div>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    data-testid={`notif-item-${n.id}`}
                    onClick={() => handleClick(n)}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "14px 18px",
                      background: n.read ? "transparent" : "var(--bg-alt)",
                      border: "none",
                      borderBottom: "1px solid var(--border-default)",
                      cursor: "pointer",
                      display: "flex",
                      gap: 12,
                      alignItems: "flex-start",
                    }}
                  >
                    {n.actor?.avatar_url ? (
                      <img
                        src={n.actor.avatar_url}
                        alt=""
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          objectFit: "cover",
                          flexShrink: 0,
                          border: "1px solid var(--border-default)",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          background: "var(--bg-alt)",
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                          border: "1px solid var(--border-default)",
                        }}
                      >
                        <Glyph kind={n.kind} />
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontFamily: "'Cormorant Garamond', serif",
                          fontSize: 16,
                          color: "var(--text-primary)",
                          lineHeight: 1.35,
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <Glyph kind={n.kind} />
                        <span>{phrase(n)}</span>
                      </div>
                      {(n.kind === "comment" || n.kind === "mention") && n.preview && (
                        <div
                          style={{
                            fontFamily: "'Cormorant Garamond', serif",
                            fontStyle: "italic",
                            fontSize: 14,
                            color: "var(--text-secondary)",
                            marginTop: 2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          &ldquo;{n.preview}&rdquo;
                        </div>
                      )}
                      <div
                        className="overline"
                        style={{ fontSize: 10, marginTop: 4, color: "var(--text-muted)" }}
                      >
                        {timeAgo(n.created_at)}
                      </div>
                    </div>
                    {n.post?.media_url && (
                      <img
                        src={n.post.media_url}
                        alt=""
                        style={{
                          width: 40,
                          height: 40,
                          objectFit: "cover",
                          flexShrink: 0,
                          border: "1px solid var(--border-default)",
                        }}
                      />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
